import { PlaybackState } from '../../../models/common/video-playback.model';

export type VideoStreamStatus = 'L' | 'S' | 'TBS';

/** Video fields that decide whether viewers see it as live. */
export interface StreamStateFields {
  stream_status?: string | null;
  playback_state?: PlaybackState | null;
  /**
   * The API's status as viewers see it (a pause counts as live only for a limited time).
   * A snapshot of the fetch: withStreamState() drops it when the state changes on the page.
   */
  effective_stream_status?: string | null;
}

/**
 * The streamer paused between matches. IVS has ended (S) or is ending (L) the session,
 * but viewers keep seeing the stream as live, behind the "Stream Paused" slate, until the API says the pause expired.
 */
export function isStreamPaused(video: StreamStateFields | null | undefined): boolean {
  return (
    video?.playback_state === 'PAUSED' &&
    (video.stream_status === 'L' || video.stream_status === 'S') &&
    (video.effective_stream_status ?? 'L') === 'L'
  );
}

/** stream_status as viewers see it: a paused stream is still live. */
export function effectiveStreamStatus(video: StreamStateFields | null | undefined): string | undefined {
  return video?.effective_stream_status ?? (isStreamPaused(video) ? 'L' : video?.stream_status ?? undefined);
}

/** The video with a stream state changed on the page; the fetched effective status no longer applies. */
export function withStreamState<T extends StreamStateFields>(
  video: T,
  changes: Pick<StreamStateFields, 'stream_status' | 'playback_state'>
): T {
  return { ...video, ...changes, effective_stream_status: undefined };
}

/** Maps API stream_status to the numeric route segment used in /live/:stream_status/:id */
export function streamStatusToRouteParam(
  streamStatus: string | undefined
): number | null {
  switch (streamStatus) {
    case 'L':
      return 1;
    case 'S':
      return 0;
    case 'TBS':
      return -1;
    default:
      return null;
  }
}

export function isUpcomingStream(streamStatus: string | undefined): boolean {
  return streamStatus === 'TBS';
}

export function isLiveStream(streamStatus: string | undefined): boolean {
  return streamStatus === 'L';
}

export function isStreamedReplay(streamStatus: string | undefined): boolean {
  return streamStatus === 'S';
}

export function isPlayableStreamStatus(streamStatus: string | undefined): boolean {
  return streamStatus === 'L' || streamStatus === 'S';
}

export function isMuxOrIvsPlatform(platform: string | undefined): boolean {
  return platform === 'MUX' || platform === 'IVS';
}

/** Scheduled start time from common BE payload shapes. */
export function getVideoScheduledStartTime(video: {
  start_time?: string;
  meta?: { start_time?: string };
  match_details?: { start_time?: string };
} | null | undefined): string | null {
  if (!video) {
    return null;
  }
  return (
    video.start_time ??
    video.meta?.start_time ??
    video.match_details?.start_time ??
    null
  );
}

/** Resolves the HLS URL to load for the current stream status. */
export function resolveVideoPlaybackUrl(video: {
  stream_status?: string | null;
  playback_id?: string | null;
  asset_playback_id?: string | null;
  source_url?: string | null;
} | null | undefined): string | null {
  if (!video || video.stream_status === 'TBS') {
    return null;
  }
  if (video.stream_status === 'L') {
    return firstPlaybackUrl(video.playback_id, video.asset_playback_id, video.source_url);
  }
  return firstPlaybackUrl(video.asset_playback_id, video.playback_id, video.source_url);
}

export function isValidPlaybackUrl(url: string | null | undefined): url is string {
  return url != null && url !== '';
}

export function isIvsPlayback(platform: string | null | undefined, sourceUrl: string): boolean {
  return (platform ?? '').toUpperCase() === 'IVS' || sourceUrl.includes('.m3u8');
}

function firstPlaybackUrl(
  ...urls: Array<string | null | undefined>
): string | null {
  return urls.find((url) => isValidPlaybackUrl(url)) ?? null;
}
